import React, {useEffect, useState} from 'react';
import FieldDescription from './FieldDescription';
import ListLoading from './ListLoading';
import {nonDismissable} from '../utils/dialogFunctions';
import {makeStyles} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import CloseIcon from '@material-ui/icons/Close';
import RemoveIcon from '@material-ui/icons/Remove';
import SaveIcon from '@material-ui/icons/Save';

const useDialogStyles = makeStyles({
    actions: {
        textAlign: 'right'
    },
    root: {
        margin: 0,
        padding: 12,
    },
    btnClose: {
        position: 'absolute',
        right: 10,
        top: 8,
        color: '#666666',
    },
    btnDialog: {
        margin: '0 0.5em 0 0.5em',
        verticalAlign: 'middle'
    },
    indicatorIcon: {
        display: 'inline-block',
        fontSize: 26,
        margin: '10px 9px 0 0'
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignContent: 'flex-start',
        margin: '0 0 1em 0'
    },
    note: {
        margin: '0 0 1em 0'
    }
});

/**
 * A Material UI Dialog to adjust a Client's Pallet Count
 *
 * The new count entered here is converted to a signed difference by the caller,
 * as the API records the adjustment and derives the running total itself.
 *
 * @param openDialog
 * @param record
 * @param saving
 * @param onCloseFunc
 * @param onSaveFunc
 * @return {JSX.Element}
 * @constructor
 */
const PalletQtyEditDialog = (
    {
        openDialog,
        record,
        saving = false,
        onCloseFunc,
        onSaveFunc
    }) => {

    const qty = (record && record.pallet_qty) ? parseFloat(record.pallet_qty) : 0;
    // The client's current pallet notes, carried on the latest ledger entry
    const currentNotes = (record && record.pallet_notes) ? record.pallet_notes : '';

    const [open, setOpen] = useState(false);
    // Kept as a string so partial decimal entries (e.g. "1.") survive typing
    const [newQty, setNewQty] = useState(String(qty));
    const [note, setNote] = useState('');
    const [palletNotes, setPalletNotes] = useState(currentNotes);
    const classes = useDialogStyles();

    useEffect(
        () => {
            // Reset the inputs each time the dialog is opened
            const resetDialog = () => {
                setNewQty(String(qty));
                setNote('');
                setPalletNotes(currentNotes);
                setOpen(openDialog);
            };

            return resetDialog();

        }, [openDialog, qty, currentNotes]
    );

    const parsedQty = parseFloat(newQty);
    const newCount = (isNaN(parsedQty) || parsedQty < 0) ? 0 : parsedQty;
    // Round to 2 decimals to match the API's numeric(12,2) pallet counts and
    // avoid float artifacts (e.g. 0.30000000000000004) in the difference
    const difference = Math.round((newCount - qty) * 100) / 100;
    const notesChanged = palletNotes !== currentNotes;
    // A count change requires a reason; a note-only change saves as a
    // zero-delta ledger entry and the API records the reason itself.
    const canSave = (difference !== 0 && note.trim() !== '') || (difference === 0 && notesChanged);

    const handleAutoSelect = (e) => {
        e.target.select();
    };

    const handleQtyChange = (e) => {
        const value = (e.target && e.target.value) ? e.target.value : '';
        // Allow only non-negative whole or decimal counts (or empty while editing)
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setNewQty(value);
        }
    };

    const handleClose = () => {
        setOpen(false);
        if (typeof onCloseFunc === 'function') {
            onCloseFunc();
        }
    };

    const handleSave = () => {
        if (typeof onSaveFunc === 'function') {
            onSaveFunc({qty: difference, info: note, notes: palletNotes});
        }
    };

    if (!record || !record.id) {
        return (
            <>
            </>
        )
    }

    const sign = (difference > 0) ? '+' : '';
    const diffStr = sign + difference;

    return (
        <>
            <Dialog
                onClose={nonDismissable(handleClose)}
                aria-labelledby="Edit-Pallet-Count-Dialog"
                open={open}
                fullWidth={true}
                maxWidth="sm"
                transitionDuration={{
                    enter: 100,
                    exit: 150
                }}
            >
                <MuiDialogTitle disableTypography className={classes.root}>
                    <Typography variant="h6">
                        Edit Pallet Count
                    </Typography>
                    <Typography>
                        {record.name}
                    </Typography>
                    <IconButton aria-label="close" className={classes.btnClose} onClick={handleClose}>
                        <CloseIcon/>
                    </IconButton>
                </MuiDialogTitle>
                <MuiDialogContent dividers>

                    {!saving &&
                    <>
                        <FieldDescription
                            instructions={true} marginBottom={true}
                            text={`Enter this Client's new pallet count and a brief reason for the change. Half pallets may be entered as decimals (e.g. 1.5). The change is recorded in the Pallet Count History.`}
                        />
                        <div className={classes.inputContainer}>
                            <TextField
                                label="Current Count"
                                value={qty}
                                className="input-inline input-120"
                                disabled
                            />
                            <TextField
                                type="number"
                                label="New Count"
                                id={`edit-pallet-qty-${record.id}`}
                                value={newQty}
                                autoFocus={true}
                                className="input-inline input-120"
                                onFocus={handleAutoSelect}
                                onChange={(e) => handleQtyChange(e)}
                                InputProps={{
                                    inputProps: {
                                        min: 0,
                                        step: 0.5,
                                        autoFocus: true
                                    }
                                }}
                            />
                            <TextField
                                label="Difference"
                                value={diffStr}
                                className="input-inline input-90 input-right"
                                disabled
                            />
                            {difference === 0 &&
                                <RemoveIcon color="primary" className={classes.indicatorIcon}/>
                            }
                            {difference < 0 &&
                                <ArrowDownwardIcon color="primary" className={classes.indicatorIcon}/>
                            }
                            {difference > 0 &&
                                <ArrowUpwardIcon color="primary" className={classes.indicatorIcon}/>
                            }
                        </div>
                        <TextField
                            label="Reason *"
                            id={`edit-pallet-note-${record.id}`}
                            value={note}
                            placeholder="added a pallet"
                            fullWidth={true}
                            className={classes.note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                        <TextField
                            label="Notes"
                            id={`edit-pallet-notes-${record.id}`}
                            value={palletNotes}
                            placeholder="department / billing breakdowns"
                            fullWidth={true}
                            multiline
                            className={classes.note}
                            onChange={(e) => setPalletNotes(e.target.value)}
                        />
                        <FieldDescription
                            description={true} marginBottom={true}
                            text="A reason is required when changing the count. Saving with an
                            unchanged count records a notes update in the history."
                        />
                        <div className={classes.actions}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => handleClose()}
                                className={classes.btnDialog}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon/>}
                                onClick={() => handleSave()}
                                disabled={!canSave}
                                className={classes.btnDialog}
                            >
                                Save Pallet Count
                            </Button>
                        </div>
                    </>
                    }

                    {saving &&
                    <>
                        <ListLoading text="Saving Pallet Count..."/>
                    </>
                    }
                </MuiDialogContent>
            </Dialog>
        </>
    )
};

export default PalletQtyEditDialog;
